import React, { useState, useEffect, useMemo } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import { Card, CardBody, Table, Input, Badge, Row, Col } from "reactstrap"
import {
  FaMobileAlt,
  FaSearch,
  FaAndroid,
  FaWifi,
  FaSync,
} from "react-icons/fa"
import { MdArrowBack, MdOutlineSmartphone } from "react-icons/md"
import Loader from "components/Loader/Loader"
import { fetchData } from "utils/Common_api_functions"
import moment from "moment"
import { Link } from "react-router-dom"
import Configs from "config"
import Swal from "sweetalert2"

const DEVICES_STYLES = `
.device-hero { border-radius: 20px; padding: 28px 32px; color: #F2F7F4;
  background: radial-gradient(500px 200px at 85% 15%, rgba(37,211,102,.22), transparent 70%), linear-gradient(125deg, #0B2B40 0%, #0D4A5C 100%); }
.device-stat { display: flex; align-items: center; gap: 0.85rem; padding: 1rem 1.35rem; }
.device-stat + .device-stat { border-left: 1px solid #E7ECEF; }
@media (max-width: 992px) {
  .device-stat + .device-stat { border-left: none; border-top: 1px solid #E7ECEF; }
}
.device-stat-icon { width: 46px; height: 46px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.device-table td, .device-table th { vertical-align: middle; }
.device-perm-badge { font-size: 0.72rem; padding: 0.2em 0.65em; letter-spacing: 0.02em; }
.device-manufacturer-chip { background: linear-gradient(135deg, #eafaf3, #d9f5e8); color: #147a4a; border: 1px solid #bdeed4; }
.device-os-chip { background: #f4f6f8; color: #47586b; border: 1px solid #dde3ea; }
.device-app-chip { background: #fff4e0; color: #8a5a10; border: 1px solid #f5ddab; }
.device-perm-dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
.device-perm-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 6px 10px; min-width: 140px; }
.device-perm-label { color: #64748b; font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
.device-perm-value { color: #0f172a; font-size: 0.82rem; font-weight: 600; }
`

const PERMISSION_META = {
  locationMode: { label: "Location Mode" },
  fineLocation: { label: "Fine Location" },
  backgroundLocation: { label: "Background Location" },
  notifications: { label: "Notifications" },
  batteryOptimizationIgnored: { label: "Battery Opt" },
}

const getPermissionColor = value => {
  const v = String(value).toLowerCase()
  if (["granted", "always", "while_in_use", "not_required", "true"].includes(v))
    return "#22c55e"
  if (["denied", "false"].includes(v)) return "#ef4444"
  return "#94a3b8"
}

const getPermissionText = (key, value) => {
  if (key === "batteryOptimizationIgnored")
    return value ? "Ignored" : "Normal"
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase())
}

const StaffDevices = () => {
  document.title = "Staff Devices | Vintage Infra Equipment Sol PVT LTD"

  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")
  const [appVersionFilter, setAppVersionFilter] = useState("all")
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    getData()
  }, [])

  async function getData() {
    try {
      const [usersRes, devicesRes] = await Promise.all([
        fetchData(setLoading, "users"),
        fetchData(setLoading, "user-device-info"),
      ])

      const users = usersRes?.success ? usersRes.data : []
      const devices = devicesRes?.success ? devicesRes.data : []

      // Merge user info with device info
      const merged = devices.map(device => {
        const user = users.find(u => u.id === device.userId) || {}
        return {
          ...device,
          user: user,
        }
      })

      setData(merged)
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setLoading(false)
    }
  }

  const appVersions = useMemo(() => {
    const versions = data
      .map(d => d?.appVersion)
      .filter(v => v !== undefined && v !== null && String(v).trim() !== "")
    return [...new Set(versions.map(String))].sort((a, b) =>
      b.localeCompare(a, undefined, { numeric: true, sensitivity: "base" })
    )
  }, [data])

  const filteredData = useMemo(() => {
    const search = searchString.toLowerCase()
    return data.filter(item => {
      if (
        appVersionFilter !== "all" &&
        String(item?.appVersion) !== appVersionFilter
      )
        return false

      return (
        item?.user?.name?.toLowerCase().includes(search) ||
        item?.user?.employeeId?.toLowerCase().includes(search) ||
        item?.user?.designation?.toLowerCase().includes(search) ||
        item?.deviceModel?.toLowerCase().includes(search) ||
        item?.manufacturer?.toLowerCase().includes(search) ||
        item?.platform?.toLowerCase().includes(search) ||
        item?.osVersion?.toLowerCase().includes(search) ||
        item?.appVersion?.toLowerCase().includes(search)
      )
    })
  }, [data, searchString, appVersionFilter])

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(() => {
    const end = start + pageSize
    return filteredData.slice(start, end)
  }, [filteredData, currentPage, pageSize, start])

  // Stats
  const uniqueStaff = useMemo(
    () => new Set(data.map(d => d.userId)).size,
    [data]
  )
  const uniqueManufacturers = useMemo(
    () => new Set(data.map(d => d.manufacturer)).size,
    [data]
  )
  const lastReported = useMemo(() => {
    const latest = data.reduce((max, d) => {
      const t = new Date(d.reportedAt).getTime()
      return t > max ? t : max
    }, 0)
    return latest > 0 ? moment(latest).format("DD MMM YYYY, hh:mm A") : "-"
  }, [data])

  const parsePermissions = permissionsString => {
    try {
      return JSON.parse(permissionsString)
    } catch {
      return {}
    }
  }

  const getInitials = name =>
    (name || "-")
      .split(" ")
      .map(p => p[0])
      .slice(0, 2)
      .join("")
      .toUpperCase()

  return (
    <div className="page-content">
      <style>{DEVICES_STYLES}</style>
      {loading && <Loader />}
      <Breadcrumb title="Staff" breadcrumbItem="Staff Devices" />

      {/* Hero */}
      <div className="device-hero mb-3">
        <Row className="align-items-center g-3">
          <Col lg={6}>
            <h3 className="mb-1 fw-bold" style={{ letterSpacing: "-0.01em" }}>
              Staff Devices
            </h3>
            <p className="mb-0" style={{ color: "#BCD8C9", maxWidth: 560 }}>
              Monitor device registrations, OS versions and app permissions
              across your team's mobile devices.
            </p>
          </Col>
          <Col lg={6}>
            <div className="d-flex gap-2 justify-content-lg-end flex-wrap">
              <div
                className="d-flex align-items-center gap-2 px-3 py-2"
                style={{
                  borderRadius: 14,
                  background: "rgba(255,255,255,.08)",
                  border: "1px solid rgba(255,255,255,.16)",
                }}
              >
                <FaMobileAlt size={20} color="#25D366" />
                <div>
                  <div
                    className="fw-bold"
                    style={{ fontSize: "1.3rem", lineHeight: 1.1 }}
                  >
                    {data.length}
                  </div>
                  <small style={{ color: "#BCD8C9" }}>Devices</small>
                </div>
              </div>
              <div
                className="d-flex align-items-center gap-2 px-3 py-2"
                style={{
                  borderRadius: 14,
                  background: "rgba(255,255,255,.08)",
                  border: "1px solid rgba(255,255,255,.16)",
                }}
              >
                <MdOutlineSmartphone size={20} color="#25D366" />
                <div>
                  <div
                    className="fw-bold"
                    style={{ fontSize: "1.3rem", lineHeight: 1.1 }}
                  >
                    {uniqueStaff}
                  </div>
                  <small style={{ color: "#BCD8C9" }}>Staff</small>
                </div>
              </div>
              <div
                className="d-flex align-items-center gap-2 px-3 py-2"
                style={{
                  borderRadius: 14,
                  background: "rgba(255,255,255,.08)",
                  border: "1px solid rgba(255,255,255,.16)",
                }}
              >
                <FaAndroid size={20} color="#25D366" />
                <div>
                  <div
                    className="fw-bold"
                    style={{ fontSize: "1.3rem", lineHeight: 1.1 }}
                  >
                    {uniqueManufacturers}
                  </div>
                  <small style={{ color: "#BCD8C9" }}>Brands</small>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Stat strip */}
      <Card className="shadow-lg mb-3">
        <div className="d-flex">
          <div className="device-stat flex-grow-1">
            <div
              className="device-stat-icon"
              style={{ backgroundColor: "#E4EDFF", color: "#1D5FD1" }}
            >
              <FaMobileAlt size={22} />
            </div>
            <div>
              <div
                className="fw-bold"
                style={{ fontSize: "1.5rem", lineHeight: 1.1 }}
              >
                {data.length}
              </div>
              <div className="text-muted small">Total devices</div>
            </div>
          </div>
          <div className="device-stat flex-grow-1">
            <div
              className="device-stat-icon"
              style={{ backgroundColor: "#DFF2E3", color: "#1C7A44" }}
            >
              <MdOutlineSmartphone size={22} />
            </div>
            <div>
              <div
                className="fw-bold"
                style={{ fontSize: "1.5rem", lineHeight: 1.1 }}
              >
                {uniqueStaff}
              </div>
              <div className="text-muted small">Registered staff</div>
            </div>
          </div>
          <div className="device-stat flex-grow-1">
            <div
              className="device-stat-icon"
              style={{ backgroundColor: "#FBEFD4", color: "#9A6B0F" }}
            >
              <FaAndroid size={22} />
            </div>
            <div>
              <div
                className="fw-bold"
                style={{ fontSize: "1.5rem", lineHeight: 1.1 }}
              >
                {uniqueManufacturers}
              </div>
              <div className="text-muted small">Device brands</div>
            </div>
          </div>
          <div className="device-stat flex-grow-1">
            <div
              className="device-stat-icon"
              style={{ backgroundColor: "#DFF5F0", color: "#0E7C6B" }}
            >
              <FaWifi size={22} />
            </div>
            <div>
              <div
                className="fw-bold"
                style={{ fontSize: "1.5rem", lineHeight: 1.1 }}
              >
                {lastReported}
              </div>
              <div className="text-muted small">Last reported</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Devices Table */}
      <Card className="shadow-lg">
        <CardBody>
          {/* Toolbar */}
          <div className="bg-light rounded-4 p-3 mb-3">
            <Row className="g-2 align-items-center">
              <Col xs="auto">
                <Link
                  to={`${Configs.appurl}staff`}
                  className="btn btn-light rounded-4 border"
                >
                  <MdArrowBack className="me-1" /> Back
                </Link>
              </Col>
              <Col xs="auto">
                <div className="position-relative">
                  <FaSearch
                    className="position-absolute text-muted"
                    style={{ left: 14, top: 11, fontSize: 13 }}
                  />
                  <Input
                    className="rounded-4 ps-5"
                    style={{ width: 280 }}
                    placeholder="Search by name, designation, device..."
                    value={searchString}
                    onChange={e => {
                      setSearchString(e.target.value)
                      setCurrentPage(1)
                    }}
                  />
                </div>
              </Col>
              <Col xs="auto">
                <Input
                  type="select"
                  value={appVersionFilter}
                  onChange={e => {
                    setAppVersionFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="rounded-4 pe-5"
                  style={{ minWidth: 150 }}
                  disabled={appVersions.length === 0}
                >
                  <option value="all">All App Versions</option>
                  {appVersions.map(version => (
                    <option key={version} value={version}>
                      v{version}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col xs="auto">
                <Input
                  type="select"
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value)), setCurrentPage(1)
                  }}
                  className="rounded-4 pe-5"
                  style={{ width: 90 }}
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>
              </Col>
            </Row>
          </div>

          {/* Table */}
          <Table bordered responsive className="align-middle device-table">
            <thead className="table-dark">
              <tr>
                <th className="text-center" style={{ width: "60px" }}>
                  #
                </th>
                <th style={{ minWidth: "220px" }}>Staff</th>
                <th style={{ minWidth: "180px" }}>Device</th>
                <th style={{ width: "160px" }}>Platform / OS</th>
                <th style={{ width: "110px" }}>App Version</th>
                <th style={{ minWidth: "400px" }}>Permissions</th>
                <th style={{ width: "190px" }}>Last Reported</th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-4">
                    No device records found
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => {
                  const perms = parsePermissions(item.permissions)
                  return (
                    <tr key={item.id}>
                      <td className="text-center">{start + index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="d-flex align-items-center justify-content-center flex-shrink-0 fw-bold"
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              background:
                                "linear-gradient(135deg, #0D4A5C, #0B2B40)",
                              color: "#fff",
                              fontSize: "0.8rem",
                            }}
                          >
                            {getInitials(item.user?.name)}
                          </span>
                          <div>
                            <div className="fw-bold">{item.user?.name}</div>
                            <div className="text-muted small">
                              {item.user?.employeeId} • {item.user?.designation}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 10,
                              background: "#f4f6f8",
                              color: "#47586b",
                            }}
                          >
                            <FaMobileAlt size={18} />
                          </div>
                          <div>
                            <span
                              className="badge device-manufacturer-chip"
                              style={{ fontSize: "0.78rem" }}
                            >
                              {item.manufacturer}
                            </span>
                            <div className="text-muted small mt-1">
                              {item.deviceModel}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          <span
                            className="badge device-os-chip"
                            style={{ fontSize: "0.78rem" }}
                          >
                            {item.platform}
                          </span>
                          <span
                            className="badge device-os-chip"
                            style={{ fontSize: "0.78rem" }}
                          >
                            Android {item.osVersion}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          className="badge device-app-chip"
                          style={{ fontSize: "0.82rem" }}
                        >
                          v{item.appVersion}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {Object.keys(PERMISSION_META).map(key => {
                            if (!(key in perms)) return null
                            const value = perms[key]
                            const color = getPermissionColor(value)
                            const text = getPermissionText(key, value)
                            return (
                              <div
                                key={key}
                                className="device-perm-card"
                                style={{ minWidth: 120 }}
                              >
                                <div className="device-perm-label">
                                  {PERMISSION_META[key].label}
                                </div>
                                <div className="d-flex align-items-center gap-1">
                                  <span
                                    className="device-perm-dot"
                                    style={{ backgroundColor: color }}
                                  />
                                  <span className="device-perm-value">
                                    {text}
                                  </span>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <div className="small fw-bold">
                          {item.reportedAt
                            ? moment(item.reportedAt).format("DD MMM YYYY")
                            : "-"}
                        </div>
                        <div className="text-muted small">
                          {item.reportedAt
                            ? moment(item.reportedAt).format("hh:mm A")
                            : ""}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <nav
            aria-label="Page navigation example"
            className="d-flex justify-content-between mt-3"
          >
            <h5>
              Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {filteredData.length} entries
            </h5>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>
    </div>
  )
}

export default StaffDevices